import { lazy, Suspense, useEffect, useMemo } from 'react';

import { useUnit } from 'effector-react';
import { useLocation } from 'react-router-dom';

import AppAlert from '@m/components/AppAlert';
import BottomAppAlert from '@m/components/BottomAppAlert';
import { useCustomerFeatureFlag } from '@m/hooks/data/featureFlags';
import { useHeapIdentifyUser } from '@m/hooks/useHeapIdentifyUser';
import useVisitPolling from '@m/hooks/useVisitPolling';
import { qrcodeStore } from '@m/state/qrcode';
import { userAnalytics } from '@m/utils/userAnalytics';
import { CiaNearbyGatePrompt } from 'apps/customer/components/GateAccess/CiaNearbyGatePrompt';
import { onboardingSessionStore } from 'apps/customer/containers/Onboarding/container/onboardingSession';
import {
  useSignInOnboardingExperiment,
  useStpOnboardingExperiment,
} from 'apps/customer/containers/Onboarding/container/useSignInOnboardingExperiment';
import { useStpOnboardingFlow } from 'apps/customer/containers/Onboarding/container/useStpOnboardingFlow';
import { resolveOnboardingExperimentCohort } from 'apps/customer/containers/Onboarding/onboardingExperimentCohort';
import { useClosestMetropolisRegion } from 'apps/customer/hooks/useClosestMetropolisRegion';
import { useGtagPageViews } from 'apps/customer/hooks/useGtagPageViews';
import { useInitialDataForUser } from 'apps/customer/hooks/useInitialDataForUser';
import { useRefreshTodoItems } from 'apps/customer/hooks/useRefreshTodoItems';
import { useStableIdAssociation } from 'apps/customer/hooks/useStableIdAssociation';
import { useUserInitialization } from 'apps/customer/hooks/useUserInitialization';
import CustomerRoutes from 'apps/customer/routes/CustomerRoutes';
import membershipStore from 'apps/customer/state/membership/store';
import { todoStore } from 'apps/customer/state/todo';
import { uiStore } from 'apps/customer/state/ui';
import { userStore } from 'apps/customer/state/user';
import { fetchTipSettings, visitStore } from 'apps/customer/state/visit';
import { getQAOnboardingOverride } from 'apps/customer/utils/qaOnboardingOverride';
import {
  isOnTreatmentSignInOnboardingPath,
  isRecognitionEnrollmentPath,
  isSignInOnboardingPath,
  isTaskOnboardingPath,
} from 'apps/customer/utils/signInOnboardingPathGate';
import { isVisitActive } from 'apps/customer/utils/visit';

import { CaptureRwgToken } from './components/CaptureRwgToken/CaptureRwgToken';
import useDoesUserHaveSubscriptions from './hooks/useDoesUserHaveSubscriptions';
import useIsFirstTimeUser from './hooks/useIsFirstTimeUser';
import { EMBEDDED_PLACEMENT_IDS } from './iterable/embedded-placements';
import { IterableInitializedProvider } from './iterable/IterableInitializedContext';
import { useEmbeddedSession } from './iterable/useEmbeddedSession';
import { useEmbeddedSync } from './iterable/useEmbeddedSync';
import { useInAppMessages } from './iterable/useInAppMessages';
import { useIterable } from './iterable/useIterable';

import type { User } from '@m/types/api';

const SideMenu = lazy(() => import('apps/customer/components/SideMenu'));
const AppFullModal = lazy(() => import('apps/customer/components/AppFullModal'));
const UserAgreementSheet = lazy(() => import('apps/customer/components/AgreementSheet'));

function Customer() {
  const location = useLocation();
  const { isAppModalVisible } = useUnit(uiStore);
  const { expressOnboardingExperimentGroup, hasFetchedTodo, onboardingTasks } = useUnit(todoStore);
  const { individualSubscriptionInvitationId } = useUnit(membershipStore);
  const { pendingSubscriptionResume } = useUnit(onboardingSessionStore);
  const {
    user,
    status: { parseInvitationCodeSuccess, getUserSuccess },
    authenticated,
    enterpriseUser,
  } = useUnit(userStore);
  const onboardingExperimentCohort = resolveOnboardingExperimentCohort({
    enterpriseUser,
    individualSubscriptionInvitationId,
    pendingSubscriptionVariant: pendingSubscriptionResume?.variant,
    search: location.search,
  });
  const qaOnboardingOverride = getQAOnboardingOverride(location.search);
  const signInOnboardingExperiment = useSignInOnboardingExperiment(
    onboardingExperimentCohort,
    isSignInOnboardingPath(location.pathname) ||
      location.pathname === '/login' ||
      onboardingExperimentCohort !== 'agnostic',
    qaOnboardingOverride,
  );
  const isOnTaskOnboardingPath = isTaskOnboardingPath(location.pathname);
  const isStpOnboarding = useStpOnboardingFlow({
    hasFetchedTodo,
    isOnTaskOnboardingPath,
    onboardingTasks,
  });
  const shouldEvaluateStpOnboarding =
    hasFetchedTodo && isStpOnboarding && isOnTaskOnboardingPath && !qaOnboardingOverride;
  const stpOnboardingExperiment = useStpOnboardingExperiment(shouldEvaluateStpOnboarding);
  const isStpExperimentPending =
    shouldEvaluateStpOnboarding && stpOnboardingExperiment.status === 'pending';
  const taskOnboardingGroup =
    qaOnboardingOverride ??
    (isStpOnboarding && isOnTaskOnboardingPath
      ? stpOnboardingExperiment.status
      : expressOnboardingExperimentGroup);
  const isTaskOnboardingTreatment = taskOnboardingGroup === 'treatment';
  const isOnSignInOnboardingTreatmentPath = isOnTreatmentSignInOnboardingPath({
    experiment: signInOnboardingExperiment,
    pathname: location.pathname,
  });

  const { visits, hasActiveVisit } = useUnit(visitStore);

  const {
    status: { handleQRCodeSuccess },
  } = useUnit(qrcodeStore);

  const shouldShowVisitMenuItem = !!hasActiveVisit;
  // Memoized so the effect below only re-fires when the set of active visit ids
  // changes, not on every render. Keyed on `visits` itself rather than a joined-id
  // string: a tighter key would also dodge the rare case where visit *content*
  // changes without the active *id set* changing, but every lint-clean way to do
  // that in this codebase's stricter hook rules (useMemo/useEffect deps rejecting a
  // key that doesn't match the referenced variable; react-hooks/refs rejecting a
  // ref-based memo) was worse than the problem -- an occasional redundant
  // tip-settings refetch is a low-severity, low-frequency tradeoff by comparison.
  const activeVisitIds = useMemo(
    () => visits.filter(isVisitActive).map((visit) => visit.id),
    [visits],
  );

  /* hide initial loader */
  useEffect(() => {
    if (
      authenticated !== null ||
      getUserSuccess !== null ||
      parseInvitationCodeSuccess !== null ||
      handleQRCodeSuccess !== null
    ) {
      document.getElementById('initial-loader')?.remove();
    }
  }, [authenticated, getUserSuccess, parseInvitationCodeSuccess, handleQRCodeSuccess]);

  useEffect(() => {
    if (authenticated && user) {
      userAnalytics.setUser(user as User);
    }
  }, [authenticated, user]);

  /* Init data */
  useUserInitialization();
  useRefreshTodoItems();
  const userDataInitialized = useInitialDataForUser(user?.id);
  // Also poll for physical card guest sessions (isRegistered=false so authenticated=false,
  // but they still have an active visit that needs real-time specialist status updates)
  useVisitPolling({
    interval: 4000,
    enabled: !!authenticated || (getUserSuccess === true && !!hasActiveVisit),
  });

  /* Handle region to display */
  useClosestMetropolisRegion();

  /* GA4 page views — base snippet sets send_page_view:false */
  useGtagPageViews();

  /* Heap tracking */
  useHeapIdentifyUser({
    authenticated,
    // TODO: fix userStore user type
    user: user as User,
  });
  useStableIdAssociation({ authenticated, userId: user?.id });

  /* Init Heap first-time-user  hook */
  useIsFirstTimeUser();
  useDoesUserHaveSubscriptions();

  /* Iterable — admin feature flags (evaluated via customer `/customer/feature/:name` API) */
  const { data: iterableCustomerAppMessageEnabled = false } = useCustomerFeatureFlag(
    'IterableCustomerAppMessage',
  );
  const { data: iterableCustomerAppEmbeddedMessagesEnabled = false } = useCustomerFeatureFlag(
    'IterableCustomerAppEmbeddedMessages',
  );

  /* Initialize Iterable SDK — userStore.user is still typed as User | empty object until TODO in store */
  const iterableUser = user && typeof user === 'object' && 'id' in user ? (user as User) : null;

  const { isInitialized: isIterableInitialized } = useIterable({
    authenticated,
    enabled: iterableCustomerAppMessageEnabled,
    user: iterableUser,
  });

  /* In-app messages — SDK handles display and tracking automatically */
  useInAppMessages({ authenticated, isInitialized: isIterableInitialized });

  /* Embedded messages — single fetch on app load (EN-11780). Placement
     components subscribe via `useEmbeddedMessages`. Mounting the placements
     onto the dashboard / offer-feed surfaces is owned by those page owners. */
  const embeddedEnabled = isIterableInitialized && iterableCustomerAppEmbeddedMessagesEnabled;
  useEmbeddedSync({
    isInitialized: embeddedEnabled,
    placementIds: EMBEDDED_PLACEMENT_IDS,
  });

  /* Embedded-message session lifecycle (EN-11787). Wraps impressions so they
     flush to Iterable as `embeddedSession` events. Driven from the app root so
     a single session spans the user's time in the app; visibility/pagehide
     boundaries are handled inside the hook. */
  useEmbeddedSession({ enabled: embeddedEnabled });

  /* Fetch tip settings for every currently active visit -- a customer can have more
     than one open at once, and each needs its own entry in tipSettingsByVisit. */
  useEffect(() => {
    activeVisitIds.forEach((activeVisitId) => {
      fetchTipSettings({ visitId: activeVisitId });
    });
  }, [activeVisitIds]);

  // authenticated is null in the initial render cycles,
  // and we intended to render nothing.
  return (
    <>
      {/* Capture the Reserve with Google token no matter which route the RwG link lands on. */}
      <CaptureRwgToken />

      <AppAlert />

      {/* Mount for guests too — park-now / home use the hamburger guest menu. */}
      {authenticated !== null && (
        <Suspense fallback={null}>
          <SideMenu
            isGuest={!authenticated}
            showConfirmation={!!authenticated && shouldShowVisitMenuItem}
            firstName={authenticated ? user.firstName : undefined}
            lastName={authenticated ? user.lastName : undefined}
          />
        </Suspense>
      )}

      {authenticated &&
        !isStpExperimentPending &&
        !isTaskOnboardingTreatment &&
        !isOnSignInOnboardingTreatmentPath &&
        !isRecognitionEnrollmentPath(location.pathname) && (
          <Suspense fallback={null}>
            <UserAgreementSheet />
          </Suspense>
        )}

      <IterableInitializedProvider isInitialized={isIterableInitialized}>
        <CustomerRoutes
          isStpOnboarding={isStpOnboarding}
          onboardingExperimentCohort={onboardingExperimentCohort}
          userDataInitialized={userDataInitialized}
          signInOnboardingExperiment={signInOnboardingExperiment}
          stpOnboardingExperiment={stpOnboardingExperiment}
        />
      </IterableInitializedProvider>

      {/* Mounted at the app root so a nearby gate can be offered on any route. */}
      {!!authenticated && <CiaNearbyGatePrompt />}

      <BottomAppAlert />

      {isAppModalVisible && (
        <Suspense fallback={null}>
          <AppFullModal />
        </Suspense>
      )}
    </>
  );
}

export default Customer;
